<template>
    <div class="container">
        <v-title :lineage="[{'Custom Fields':'custom-fields'}]">
            {{isFormUpdating && !repeatMessage ? 'Update' : 'Add'}} Custom Field
            <div slot="actions">
                <router-link class="btn inline-s" :to="{name: 'custom-fields', params: {resource: resource}}">
                    Cancel
                </router-link>
                <r-button
                    type="primary"
                    @click="save"
                    :loading="isLoading">Save Custom Field
                </r-button>
            </div>
        </v-title>
        <v-inline-notifications
            v-if="repeatMessage && !customField.isUsed && !serverError"
            type="positive"
            title="Custom field successfully created"
            class="stack-l">
            <span slot="content">
                You can add another custom field item right away or go back to the <router-link
                :to="{name: 'custom-fields', params: {resource: resource}}">custom fields list</router-link>?
            </span>
        </v-inline-notifications>
        <div v-if="customField.isUsed" class="message message-warning stack-l">
            <div class="message-content">
                Editing of this custom field is limited because it might already be in use in a resource.
            </div>
        </div>
        <v-error-messages
            :has-frontend-errors="$v.customField.$error || $v.selectedResource.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.selectedResource.required">Custom field resource is required</li>
            <li class="list-item" v-if="!$v.customField.type.required">Custom field type is required</li>
            <li class="list-item" v-if="!$v.customField.name.required">Custom field name is required</li>
            <li class="list-item" v-if="!$v.customField.name.allowedChars">Custom field name can only contain letters,
                spaces or dashes.
            </li>
            <li class="list-item" v-if="enableRestrictions && !$v.customField.additionalSchema.required">Allowed values
                are required
            </li>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Custom Field Information</h2>
            <p slot="description" class="text-muted">Create additional custom fields for particular resources. You may
                name, describe, and determine the type of the schema.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                data-cy="Resource input"
                                label="Resource"
                                :options="resourceList"
                                :disabled="customField.isUsed"
                                placeholder="Select Resource"
                                :validate="$v.selectedResource"
                                v-model="selectedResource"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                data-cy="Type input"
                                label="Type"
                                :auto-focus="!isFormUpdating"
                                :options="customFieldsTypes"
                                :disabled="customField.isUsed || isFormUpdating"
                                placeholder="Select Type"
                                :value="customField.type"
                                :validate="$v.customField.type"
                                @input="updateType"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div v-if="selectedType">
            <div class="divider"></div>
            <v-form-tile>
                <h2 slot="title">Configure {{selectedType.label}} Custom Field</h2>
                <p slot="description" class="text-muted stack-m">
                    {{selectedType.description}}
                </p>
                <div slot="description" class="tile tile-secondary">
                    <div class="tile-content inset-default">
                        <h3 class="stack-m">Preview</h3>
                        <p class="stack-m text-muted">This will help you visualize the outcome of the custom field.</p>
                        <v-custom-field :entry="customField" :preview="true"/>
                    </div>
                </div>
                <div slot="content" class="tile">
                    <div class="tile-content">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    data-cy="Field Label input"
                                    label="Field Label"
                                    help-text="You will not be able to change the label, once the field is created."
                                    :disabled="customField.isUsed || isFormUpdating"
                                    v-model="customField.name"
                                    :validate="$v.customField.name"
                                />
                            </div>
                            <div class="grid-item grid-item-full">
                                <r-input
                                    data-cy="Description input"
                                    label="Description"
                                    :multiline="true"
                                    rows="4"
                                    v-model="customField.description"
                                />
                            </div>
                            <div v-if="selectedType.value === 'string' || selectedType.value === 'array'"
                                 class="grid-item grid-item-half grid-item-break-half">
                                <v-checkbox
                                    label="Enable Restrictions"
                                    :disabled="customField.isUsed"
                                    :value="true"
                                    v-model="customField.enableRestrictions"
                                />
                            </div>
                            <div v-if="customField.enableRestrictions"
                                 class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    label="Allowed Values"
                                    :multiline="true"
                                    :disabled="customField.isUsed"
                                    rows="6"
                                    v-model="allowedValues"
                                    help-text="Add a new line to create an option"
                                    :validate="$v.customField.additionalSchema"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </v-form-tile>
        </div>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <router-link class="btn inline-s" :to="{name: 'custom-fields', params: {resource: resource}}">
                    Cancel
                </router-link>
                <r-button
                    type="primary"
                    @click="save"
                    :loading="isLoading">Save Custom Field
                </r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import vCustomField from '@/components/custom-fields/v-custom-field';
    import {
        vCheckbox,
        vErrorMessages,
        vFormTile,
        vIcon,
        vIconSprites,
        vSelect,
    } from '@/components/form';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import {required} from 'vuelidate/lib/validators';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import CustomFieldsModel from '@/common/models/custom-fields-model';
    import {CUSTOM_FIELDS_RESOURCES_LIST} from '@/data/custom-fields-resources';
    import CUSTOM_FIELDS_TYPES from '@/data/custom-fields-types';
    import _ from '@/common/helpers';

    export default {
        name: 'custom-field-form',
        mixins: [FormLoaderMixin],
        components: {
            vTitle,
            vCustomField,
            vIconSprites,
            vIcon,
            vCheckbox,
            vInlineNotifications,
            vErrorMessages,
            vFormTile,
            vSelect,
        },
        props: {
            resource: {
                type: String,
                default: null,
            },
            name: {
                type: String,
                default: null,
            },
            continue: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                customField: new CustomFieldsModel(),
                resourceList: [...CUSTOM_FIELDS_RESOURCES_LIST],
                customFieldsTypes: [...CUSTOM_FIELDS_TYPES],
                selectedResource: this.resource,
                enableRestrictions: null,
                previewUpdateTimer: null,
            };
        },
        validations() {
            const validation = {
                selectedResource: {required},
                customField: {
                    type: {required},
                    name: {
                        required,
                        allowedChars(value) {
                            return /^[\w-\s]+$/.test(value);
                        },
                    },
                },
            };
            if (this.customField.enableRestrictions) {
                validation.customField.additionalSchema = {required};
            }
            return validation;
        },
        computed: {
            predicate() {
                return this.resource !== null && this.name !== null;
            },
            repeatMessage() {
                return this.continue !== null;
            },
            /* eslint-disable func-names, object-shorthand, no-return-assign */
            allowedValues: {
                get: function () {
                    const {additionalSchema} = this.customField;
                    if (additionalSchema && additionalSchema.allowedValues) {
                        return additionalSchema.allowedValues.join('\n');
                    }
                    return '';
                },
                set: function (values) {
                    clearTimeout(this.previewUpdateTimer);
                    this.previewUpdateTimer = setTimeout(
                        () => this.customField.additionalSchema = this.parseAllowedValues(values),
                        500,
                    );
                },
            },
            selectedType() {
                if (this.customField.type) {
                    return this.customFieldsTypes.find(type => type.value === this.customField.type);
                }
                return null;
            },
        },
        methods: {
            updateType(value) {
                this.customField.enableRestrictions = false;
                this.customField.additionalSchema = null;
                this.customField.type = value;
            },
            /* eslint-disable arrow-body-style */
            parseAllowedValues(values) {
                const pattern = /^([^\n]+?)(?:\n?)$/gm;
                let preview = values.match(pattern) || [];
                preview = preview.map(word => _.trim(word)
                    .replace(/\n/g, ''));
                return {
                    allowedValues: [...new Set(preview)],
                };
            },
            async save() {
                this.$v.$touch();
                if (!this.$v.customField.$error && !this.$v.selectedResource.$error) {
                    this.isLoading = true;
                    try {
                        const params = this.customField.getPayload();
                        const payload = {
                            resource: this.selectedResource,
                            name: params.name,
                            data: params,
                        };
                        const method = this.isFormUpdating ? 'update' : 'create';
                        await this.$api(({customFields}) => customFields[method](payload));
                        window.scrollTo(0, 0);
                        if (!this.isFormUpdating) {
                            this.$router.push({
                                name: 'add-custom-field',
                                params: {
                                    resource: this.resource,
                                    continue: 'continue',
                                },
                            });
                            this.customField = new CustomFieldsModel();
                            this.$v.$reset();
                        } else {
                            this.$toast.positive('Custom field successfully updated');
                            this.$router.push({
                                name: 'update-custom-field',
                                params: {
                                    resource: this.resource,
                                    name: this.name,
                                },
                            });
                        }
                        this.isLoading = false;
                    } catch (error) {
                        this.serverError = error;
                    } finally {
                        // invalidate custom field cache
                        this.$store.dispatch('rulesEngine/invalidateCustomFields');
                        this.isLoading = false;
                    }
                }
            },
            async fetchData() {
                if (!this.isFormUpdating) {
                    return;
                }
                try {
                    const customField = await this.$api(({customFields}) => customFields.get({
                        resource: this.resource,
                        name: this.name,
                    }));
                    this.customField = new CustomFieldsModel(customField.fields);
                } catch (error) {
                    this.$redirectOnError(error, {name: 'custom-fields'});
                }
            },
        },
        watch: {
            selectedResource: function watchSelectedResource() {
                this.customField = new CustomFieldsModel();
                this.$v.$reset();
                this.$router.push({
                    name: 'add-custom-field',
                    params: {resource: this.selectedResource},
                });
            },
        },
    };
</script>
